import { ReactNode, useId } from 'react';
import Input from '../Input/Input';

export interface EditableFieldProps {
    label: string;
    value: string;
    editable: boolean;
    onChange: (value: string) => void;
    placeholder?: string;
    errorMessage?: string;
    maxLength?: number;
    type?: 'text' | 'textarea';
    textAreaRows?: number;
    prefix?: ReactNode;
    labelSlot?: ReactNode;
    footnote?: ReactNode;
    groupClassName?: string;
    labelClassName?: string;
    labelRowClassName?: string;
    containerClassName?: string;
    inputClassName?: string;
    readOnlyInputClassName?: string;
    prefixRowClassName?: string;
    /** Overrides the generated control id; only needed when something outside must reference it. */
    inputId?: string;
}

const noop = () => {};

function EditableField({
    label,
    value,
    editable,
    onChange,
    placeholder,
    errorMessage,
    maxLength,
    type,
    textAreaRows,
    prefix,
    labelSlot,
    footnote,
    groupClassName,
    labelClassName,
    labelRowClassName,
    containerClassName,
    inputClassName,
    readOnlyInputClassName,
    prefixRowClassName,
    inputId,
}: EditableFieldProps) {
    // A visible label only names its control when the two are associated — otherwise a screen
    // reader announces an unlabelled text box and "PAN" as loose text beside it.
    const generatedId = useId();
    const controlId = inputId ?? generatedId;

    const input = (
        <Input
            id={controlId}
            placeholder={editable ? placeholder : ''}
            value={value}
            onChange={editable ? onChange : noop}
            showFloatingLabel={false}
            allowAllCharacters
            disabled={!editable}
            maxLength={maxLength}
            type={type}
            textAreaRows={textAreaRows}
            isValid={!errorMessage}
            errorMessage={errorMessage}
            containerClassName={containerClassName}
            inputClassName={editable ? inputClassName : `${inputClassName ?? ''} ${readOnlyInputClassName ?? ''}`.trim()}
        />
    );

    return (
        <div className={groupClassName}>
            {labelSlot ? (
                <div className={labelRowClassName}>
                    <label className={labelClassName} htmlFor={controlId}>
                        {label}
                    </label>
                    {labelSlot}
                </div>
            ) : (
                <label className={labelClassName} htmlFor={controlId}>
                    {label}
                </label>
            )}

            {prefix ? (
                <div className={prefixRowClassName}>
                    {prefix}
                    {input}
                </div>
            ) : (
                input
            )}

            {footnote}
        </div>
    );
}

export default EditableField;
